import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { unifiedApiService } from '../../services/unified';
import { queryKeys } from '../../lib/queryClient';
import type {
  Interview,
  CreateInterviewRequest,
  UpdateInterviewRequest,
  GoogleMeetLinkResponse,
  InterviewListResponse,
  MessageResponse,
} from '../../services/unified/types';

/**
 * Query hook for getting interview details
 */
export const useInterview = (interviewId: string) => {
  return useQuery({
    queryKey: [...queryKeys.admin.all, 'interviews', interviewId],
    queryFn: (): Promise<Interview> => {
      return unifiedApiService.interviews.getInterview(interviewId);
    },
    enabled: !!interviewId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Query hook for getting interviews for a candidate
 */
export const useCandidateInterviews = (candidateId: string) => {
  return useQuery({
    queryKey: [...queryKeys.admin.all, 'candidates', candidateId, 'interviews'],
    queryFn: (): Promise<InterviewListResponse> => {
      return unifiedApiService.interviews.getCandidateInterviews(candidateId);
    },
    enabled: !!candidateId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

/**
 * Query hook for getting interviews for a job
 */
export const useJobInterviews = (jobId: string) => {
  return useQuery({
    queryKey: [...queryKeys.admin.all, 'jobs', jobId, 'interviews'],
    queryFn: (): Promise<InterviewListResponse> => {
      return unifiedApiService.interviews.getJobInterviews(jobId);
    },
    enabled: !!jobId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

/**
 * Mutation hook for scheduling an interview
 */
export const useScheduleInterview = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: CreateInterviewRequest): Promise<Interview> => {
      return unifiedApiService.interviews.scheduleInterview(data);
    },
    onSuccess: (interview) => {
      // Invalidate interview lists to show new interview
      queryClient.invalidateQueries({ 
        queryKey: [...queryKeys.admin.all, 'interviews'] 
      });
      
      // Invalidate candidate and job interviews
      if (interview.candidate_id) {
        queryClient.invalidateQueries({ 
          queryKey: [...queryKeys.admin.all, 'candidates', interview.candidate_id, 'interviews'] 
        });
        // Invalidate candidate applications to update stage (exact key match)
        queryClient.invalidateQueries({ 
          queryKey: [...queryKeys.admin.all, 'candidates', interview.candidate_id, 'applications'] 
        });
        // Also invalidate all candidate queries
        queryClient.invalidateQueries({ 
          queryKey: [...queryKeys.admin.all, 'candidates', interview.candidate_id] 
        });
      }
      if (interview.job_id) {
        queryClient.invalidateQueries({ 
          queryKey: [...queryKeys.admin.all, 'jobs', interview.job_id, 'interviews'] 
        });
        // Invalidate job candidates to update stage
        queryClient.invalidateQueries({ 
          queryKey: [...queryKeys.admin.all, 'jobs', interview.job_id, 'candidates'] 
        });
        // Invalidate all job queries
        queryClient.invalidateQueries({ 
          queryKey: [...queryKeys.admin.all, 'jobs', interview.job_id] 
        });
      }
      
      // Invalidate all candidates queries to ensure UI updates
      queryClient.invalidateQueries({ 
        queryKey: [...queryKeys.admin.all, 'candidates'] 
      });
      
      // Invalidate all admin queries as a fallback
      queryClient.invalidateQueries({ 
        queryKey: queryKeys.admin.all 
      });
    },
  });
};

/**
 * Mutation hook for updating an interview with optimistic updates
 */
export const useUpdateInterview = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({
      interviewId,
      data,
    }: {
      interviewId: string;
      data: UpdateInterviewRequest;
    }): Promise<Interview> => {
      return unifiedApiService.interviews.updateInterview(interviewId, data);
    },
    // Optimistic update
    onMutate: async ({ interviewId, data }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ 
        queryKey: [...queryKeys.admin.all, 'interviews', interviewId] 
      });
      
      // Snapshot the previous value
      const previousInterview = queryClient.getQueryData<Interview>(
        [...queryKeys.admin.all, 'interviews', interviewId]
      );
      
      // Optimistically update to the new value
      if (previousInterview) {
        queryClient.setQueryData<Interview>(
          [...queryKeys.admin.all, 'interviews', interviewId],
          {
            ...previousInterview,
            ...data,
            panel_members: typeof data.panel_members === 'string' 
              ? data.panel_members 
              : JSON.stringify(data.panel_members || [])
          } as Interview
        );
      }
      
      // Return context with the snapshot
      return { previousInterview };
    },
    onSuccess: (updatedInterview, variables) => {
      // Update the specific interview in cache with server response
      queryClient.setQueryData<Interview>(
        [...queryKeys.admin.all, 'interviews', variables.interviewId],
        {
          ...updatedInterview,
          panel_members: typeof updatedInterview.panel_members === 'string' 
            ? updatedInterview.panel_members 
            : JSON.stringify(updatedInterview.panel_members || [])
        } as Interview
      );
      
      // Invalidate interview lists to reflect changes
      queryClient.invalidateQueries({ 
        queryKey: [...queryKeys.admin.all, 'interviews'] 
      });
      queryClient.invalidateQueries({ 
        queryKey: [...queryKeys.admin.all, 'candidates'] 
      });
      queryClient.invalidateQueries({ 
        queryKey: [...queryKeys.admin.all, 'jobs'] 
      });
    },
    // Rollback on error
    onError: (_err, { interviewId }, context) => {
      if (context?.previousInterview) {
        queryClient.setQueryData(
          [...queryKeys.admin.all, 'interviews', interviewId],
          context.previousInterview
        );
      }
    },
  });
};

/**
 * Mutation hook for canceling an interview
 */
export const useCancelInterview = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (interviewId: string): Promise<MessageResponse> => {
      return unifiedApiService.interviews.cancelInterview(interviewId);
    },
    onSuccess: (_, interviewId) => {
      // Remove the interview from cache
      queryClient.removeQueries({ 
        queryKey: [...queryKeys.admin.all, 'interviews', interviewId] 
      });
      
      // Invalidate interview lists to reflect cancellation
      queryClient.invalidateQueries({ 
        queryKey: [...queryKeys.admin.all, 'interviews'] 
      });
      queryClient.invalidateQueries({ 
        queryKey: [...queryKeys.admin.all, 'candidates'] 
      });
      queryClient.invalidateQueries({ 
        queryKey: [...queryKeys.admin.all, 'jobs'] 
      });
    },
  });
};

/**
 * Mutation hook for creating Google Meet link
 */
export const useCreateGoogleMeetLink = () => {
  return useMutation({
    mutationFn: (interviewData: {
      scheduled_date: string;
      duration_minutes: number;
      attendees: string[];
      summary: string;
    }): Promise<GoogleMeetLinkResponse> => {
      return unifiedApiService.interviews.createGoogleMeetLink(interviewData);
    },
    onError: (error) => {
      console.error('Google Meet link creation failed:', error);
    },
  });
};

/**
 * Query hook for getting all panelists (for dropdown suggestions)
 */
export const usePanelists = () => {
  return useQuery({
    queryKey: [...queryKeys.admin.all, 'panelists'],
    queryFn: () => {
      return unifiedApiService.interviews.getPanelists();
    },
    staleTime: 10 * 60 * 1000, // 10 minutes - panelists don't change often
  });
};
